import { useCallback, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { MyContext } from '../../context';
import SettingsForm from '../../components/SettingsForm';
import { getStorePaceAccount, saveStorePaceAccount, triggerStoreProductSync } from '../../utils/queries';
import { useCan } from '../../hooks/useCan';
import { PLATFORMADMIN_ROLES } from '../../components/navConfig';

const FIELDS = [
	{ key: 'paceCustomerNumber', label: 'ERP Customer Number', required: true },
	{ key: 'paceApiEndpointRef', label: 'ERP API Endpoint', required: true },
	{ key: 'paceSoapUsernameRef', label: 'ERP SOAP Username', required: true },
];

export default function PaceAccountSettings() {
	const { activeStoreId } = useContext(MyContext);
	const canEdit = useCan(PLATFORMADMIN_ROLES);
	const [syncNotice, setSyncNotice] = useState('');
	const [syncing, setSyncing] = useState(false);
	const [syncFailed, setSyncFailed] = useState(false);
	const load = useCallback(() => getStorePaceAccount(activeStoreId), [activeStoreId]);
	const save = useCallback((fields) => saveStorePaceAccount(activeStoreId, fields), [activeStoreId]);

	// Kick off a catalog pull as soon as the ERP connection is saved, so the
	// customer's ERP products land in erp_products for review/publish on
	// /products without the user having to find the manual sync button there.
	const handleSaved = useCallback(async () => {
		toast.success('Sucessfully saved your changes.');
		setSyncing(true);
		setSyncFailed(false);
		setSyncNotice('Pulling your product catalog from ERP in the background — check All Products shortly to review and publish items.');
		const result = await triggerStoreProductSync(activeStoreId);
		setSyncing(false);
		// The API responds 200 even when the sync itself failed for this store --
		// the real outcome is nested in data.result, so check it too rather than
		// leaving the "pulling in the background" notice up when nothing happened.
		if (!result?.success) {
			setSyncFailed(true);
			setSyncNotice(result?.error?.message || 'Failed to sync products from ERP.');
			return;
		}
		const syncErrors = result.data?.result?.errors || [];
		if (syncErrors.length > 0) {
			setSyncFailed(true);
			setSyncNotice(syncErrors.map((e) => e.message).join(' '));
			return;
		}
		setSyncNotice('Catalog sync complete — check All Products to review and publish new items.');
	}, [activeStoreId]);

	return (
		<div className='py-8 pr-8'>
			<h1 className='text-2xl font-light tracking-tight mb-6'>ERP Credentials</h1>

			{syncNotice && (
						<div
							className={`mt-4 max-w-lg flex items-start gap-2 rounded-md border px-4 py-3 text-sm ${
								syncFailed ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'
							}`}
						>
							{syncing ? (
								<Loader2 className='size-4 shrink-0 mt-0.5 animate-spin' />
							) : syncFailed ? (
								<AlertTriangle className='size-4 shrink-0 mt-0.5' />
							) : (
								<CheckCircle2 className='size-4 shrink-0 mt-0.5' />
							)}
							<span dangerouslySetInnerHTML={{ __html: syncNotice }} />
						</div>
			)}
			
			{activeStoreId ? (
				<SettingsForm
					load={load}
					save={save}
					fields={FIELDS}
					emptyMessage='No ERP/ERP account configured yet.'
					onSaved={handleSaved}
					canEdit={canEdit}
					hideSavedMessage
				/>
			) : (
				<p className='text-sm text-gray-500'>No store selected.</p>
			)}
		</div>
	);
}
